import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import Index from "./pages/Index";
import ChatBot from "./components/ChatBot";

// Lazy load non-critical routes
const Cart = lazy(() => import("./pages/Cart"));
const Products = lazy(() => import("./pages/Products"));
const Auth = lazy(() => import("./pages/Auth"));
const Orders = lazy(() => import("./pages/Orders"));
const CheckoutSuccess = lazy(() => import("./pages/CheckoutSuccess"));
const Spotify = lazy(() => import("./pages/Spotify"));
const ArcRaiders = lazy(() => import("./pages/ArcRaiders"));
const Valorant = lazy(() => import("./pages/Valorant"));
const RocketLeague = lazy(() => import("./pages/RocketLeague"));
const LeagueOfLegends = lazy(() => import("./pages/LeagueOfLegends"));
const ClashRoyal = lazy(() => import("./pages/ClashRoyal"));
const MarvelRivals = lazy(() => import("./pages/MarvelRivals"));
const FAQ = lazy(() => import("./pages/FAQ"));
const TOS = lazy(() => import("./pages/TOS"));
const Privacy = lazy(() => import("./pages/Privacy"));
const AdminLayout = lazy(() => import("./pages/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminProducts = lazy(() => import("./pages/admin/AdminProducts"));
const AdminOrders = lazy(() => import("./pages/admin/AdminOrders"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminReviews = lazy(() => import("./pages/admin/AdminReviews"));
const AdminServices = lazy(() => import("./pages/admin/AdminServices"));

const queryClient = new QueryClient();

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

// ChatBot wrapper that hides on admin routes
const ChatBotWrapper = () => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');
  
  if (isAdminRoute) return null;
  return <ChatBot />;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <AuthProvider>
        <div className="min-h-screen bg-background">
          <Toaster />
          <Sonner 
            position="top-right" 
            toastOptions={{
              classNames: {
                toast: "glass border-border/50 shadow-xl",
                title: "text-foreground font-medium",
                description: "text-muted-foreground",
                success: "!bg-primary/10 !border-primary/30",
                error: "!bg-destructive/10 !border-destructive/30",
              },
            }}
          />
          <BrowserRouter>
            <ChatBotWrapper />
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/cart" element={<Cart />} />
                <Route path="/products" element={<Products />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/orders" element={<Orders />} />
                <Route path="/checkout-success" element={<CheckoutSuccess />} />
                <Route path="/spotify" element={<Spotify />} />
                <Route path="/arc-raiders" element={<ArcRaiders />} />
                <Route path="/valorant" element={<Valorant />} />
                <Route path="/rocket-league" element={<RocketLeague />} />
                <Route path="/league-of-legends" element={<LeagueOfLegends />} />
                <Route path="/clash-royal" element={<ClashRoyal />} />
                <Route path="/marvel-rivals" element={<MarvelRivals />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/tos" element={<TOS />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/admin" element={<AdminLayout />}>
                  <Route index element={<AdminDashboard />} />
                  <Route path="products" element={<AdminProducts />} />
                  <Route path="services" element={<AdminServices />} />
                  <Route path="orders" element={<AdminOrders />} />
                  <Route path="users" element={<AdminUsers />} />
                  <Route path="reviews" element={<AdminReviews />} />
                </Route>
              </Routes>
            </Suspense>
          </BrowserRouter>
        </div>
      </AuthProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
